import { useState } from "react";
import { useNavigate } from "react-router";
import { Lock, Settings, ArrowUpCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/shared/Toast";
import { useFeatureGateContext } from "@/contexts/FeatureGateContext";
import { getUpgradePlanForFeature, requestSubscriptionChange } from "@/services/subscription.service";
import { FEATURE_METADATA, isAddonActivatedPracticeFeature } from "@/types/practice";
import type { PracticeFeatureKey, SubscriptionPlan } from "@/types/practice";
import { cn } from "@/lib/utils";

const PLAN_LABELS: Record<SubscriptionPlan, string> = {
    none: "Kein Plan",
    starter: "Starter",
    professional: "Professional",
    enterprise: "Enterprise",
};

interface UpgradeWallProps {
    featureKey: PracticeFeatureKey;
    variant: "upgrade" | "disabled";
}

export function UpgradeWall({ featureKey, variant }: UpgradeWallProps) {
    const navigate = useNavigate();
    const toast = useToast();
    const { plan, refreshFeatures } = useFeatureGateContext();
    const [upgradeLoading, setUpgradeLoading] = useState(false);
    const meta = FEATURE_METADATA[featureKey];

    const isUpgrade = variant === "upgrade";
    const requiresAddonActivation = isUpgrade && isAddonActivatedPracticeFeature(featureKey);
    const upgradePlan = isUpgrade && !requiresAddonActivation ? getUpgradePlanForFeature(featureKey) : null;

    async function handleUpgrade() {
        if (requiresAddonActivation) {
            navigate("/app/settings?tab=tarif");
            return;
        }

        if (!upgradePlan) {
            toast.error("Kein Upgrade-Pfad gefunden", "Für dieses Modul ist derzeit kein Plan hinterlegt.");
            return;
        }

        setUpgradeLoading(true);
        try {
            const result = await requestSubscriptionChange(upgradePlan);
            if (!result.success) {
                toast.error("Upgrade konnte nicht gestartet werden", result.error);
                return;
            }

            if (result.upgraded) {
                await refreshFeatures();
                toast.success(
                    "Plan aktualisiert",
                    `Das Modul ${meta.label} steht jetzt im ${PLAN_LABELS[upgradePlan]}-Plan bereit.`,
                );
            }
        } finally {
            setUpgradeLoading(false);
        }
    }

    return (
        <div className="flex h-full min-h-[400px] items-center justify-center p-6">
            <div
                className={cn(
                    "mx-auto flex max-w-lg flex-col items-center gap-6 rounded-2xl border p-10 text-center shadow-soft",
                    isUpgrade
                        ? "border-brand-200 bg-gradient-to-b from-brand-50 to-white dark:border-brand-800 dark:from-brand-950 dark:to-neutral-900"
                        : "border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900",
                )}
            >
                <div
                    className={cn(
                        "flex h-16 w-16 items-center justify-center rounded-2xl",
                        isUpgrade
                            ? "bg-brand-100 dark:bg-brand-900"
                            : "bg-neutral-100 dark:bg-neutral-800",
                    )}
                >
                    {isUpgrade ? (
                        <Lock className="h-8 w-8 text-brand-500" />
                    ) : (
                        <Settings className="h-8 w-8 text-neutral-500 dark:text-neutral-400" />
                    )}
                </div>

                <div className="space-y-2">
                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                        {isUpgrade ? "Funktion nicht verfügbar" : `${meta.label} ist deaktiviert`}
                    </h2>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        {requiresAddonActivation
                            ? `„${meta.label}" wird erst nach Freischaltung im Bereich Tarif & Module aktiviert.`
                            : isUpgrade
                            ? `„${meta.label}" ist nicht in Ihrem aktuellen ${PLAN_LABELS[plan]}-Plan enthalten.`
                            : `„${meta.label}" ist in Ihrem Plan enthalten, aber derzeit deaktiviert.`}
                    </p>
                </div>

                <div className="flex items-start gap-3 rounded-xl bg-neutral-50 p-4 text-left dark:bg-neutral-800">
                    <ArrowUpCircle className="mt-0.5 h-5 w-5 shrink-0 text-brand-500" />
                    <div className="space-y-1">
                        <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            {meta.description}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                            {meta.roi}
                        </p>
                    </div>
                </div>

                <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
                    {isUpgrade ? (
                        <Button
                            variant="primary"
                            size="lg"
                            className="min-h-[44px] min-w-[44px] gap-2"
                            onClick={handleUpgrade}
                            loading={upgradeLoading}
                            aria-label={requiresAddonActivation ? "Addon freischalten" : "Plan upgraden"}
                        >
                            <ArrowUpCircle className="h-5 w-5" />
                            {requiresAddonActivation
                                ? "Addon freischalten"
                                : upgradePlan
                                  ? `Auf ${PLAN_LABELS[upgradePlan]} upgraden`
                                  : "Plan upgraden"}
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            size="lg"
                            className="min-h-[44px] min-w-[44px] gap-2"
                            onClick={() => navigate("/app/settings?tab=features")}
                            aria-label="In Einstellungen aktivieren"
                        >
                            <Settings className="h-5 w-5" />
                            In Einstellungen aktivieren
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="lg"
                        className="min-h-[44px] min-w-[44px]"
                        onClick={() => navigate("/app/dashboard")}
                        aria-label="Zurück zum Dashboard"
                    >
                        Zurück
                    </Button>
                </div>
            </div>
        </div>
    );
}
